import { FC, useEffect, useMemo, useRef, useState } from "react";
import Highcharts, { PlotColumnDataLabelsOptions } from "highcharts";
import { Box } from "@mui/material";
import { getTooltipOptions } from "../common/utils/tooltip/getTooltipOptions";
import { plotOptionsSeries } from "../common/utils/plot-options/plotOptionsSeries";
import { chartDefaultConfigs } from "../common/utils/constants/chartDefaultConfigs";
import {
  chartXAxisConfigs,
  chartXAxisLabels,
  xAxisFormatterEmptyPlaceholder,
} from "../common/utils/chart-axises/chartXAxis";
import {
  chartYAxisConfigs,
  chartYAxisDefaultPlotLine,
  chartYAxisLabels,
  getChartYAxisLabelsDefaultFormatter,
} from "../common/utils/chart-axises/chartYAxis";
import { useHighchartsContext } from "../common/components/HighchartsProvider";
import { HighChartsCommonProps } from "../common/utils/types/common";
import { highChartColors } from "../common/utils/constants/chartColors";
import { getChartCumulativeSeries } from "../common/utils/constants/getChartCumulativeSeries";
import { useDynamicXAxisStep } from "../common/utils/hooks/useDynamicXAxisStep";
import { calculateEstimatedLabelWidth } from "@/storybook/c8r-highcharts/common/utils/helpers/calculateEstimatedLabelWidth";

interface HighchartsColumnChartProps extends HighChartsCommonProps {
  columnDataLabels?: PlotColumnDataLabelsOptions;
}

export const HighchartsColumnChart: FC<HighchartsColumnChartProps> = ({
  chartData,
  tooltipMarkupGenerator,
  percentBased,
  yAxisLabelsFormatter,
  onChartZoom,
  chartScale,
  columnDataLabels,
  xAxisLabelsFormatter,
  yAxisTickAmount,
  cumulativeLine,
  maxHeight,
  tooltipOptions,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [acceptFormatter, setAcceptFormatter] = useState(false);

  const { chartApi, setChartApi } = useHighchartsContext();

  const series = useMemo(() => {
    return cumulativeLine
      ? [...chartData.series, getChartCumulativeSeries(chartData)]
      : chartData.series;
  }, [cumulativeLine, chartData]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    const chart = Highcharts.chart(chartRef.current, {
      chart: {
        type: "column",
        zoomType: "x",
        events: { selection: onChartZoom },
      },

      colors: highChartColors,

      title: { text: "" },

      xAxis: {
        categories: chartData.categories,
        labels: {
          enabled: true,
          ...chartXAxisLabels,
          formatter: acceptFormatter
            ? xAxisLabelsFormatter
            : xAxisFormatterEmptyPlaceholder,
        },
        ...chartXAxisConfigs,
      },

      yAxis: {
        type: chartScale,
        tickAmount: yAxisTickAmount,
        min: percentBased ? 0 : undefined,
        max: percentBased ? 100 : undefined,
        title: { text: "" },
        labels: {
          formatter:
            yAxisLabelsFormatter ??
            getChartYAxisLabelsDefaultFormatter(percentBased),
          ...chartYAxisLabels,
        },
        plotLines: [chartYAxisDefaultPlotLine],

        ...chartYAxisConfigs,
      },

      series: series,

      tooltip: tooltipMarkupGenerator
        ? getTooltipOptions({
            tooltipMarkupGenerator,
            tooltipOptions,
          })
        : { enabled: false },

      plotOptions: {
        column: {
          borderWidth: 0,
          minPointLength: 5,
          groupPadding: 0.1,
          pointPadding: 0.05,
          dataLabels: columnDataLabels,
        },
        series: plotOptionsSeries,
      },

      ...chartDefaultConfigs,
    });

    setChartApi(chart);
  }, [
    chartScale,
    chartData,
    setChartApi,
    yAxisTickAmount,
    onChartZoom,
    percentBased,
    columnDataLabels,
    yAxisLabelsFormatter,
    tooltipMarkupGenerator,
    xAxisLabelsFormatter,
    series,
    acceptFormatter,
    tooltipOptions,
  ]);

  const averageCategoryWidth = useMemo(() => {
    return calculateEstimatedLabelWidth(chartData.categories);
  }, [chartData.categories]);

  const firstRenderCallback = () => setAcceptFormatter(true);

  useDynamicXAxisStep(
    chartApi,
    chartRef,
    chartData.categories.length,
    averageCategoryWidth,
    firstRenderCallback,
  );

  return (
    <Box
      ref={chartRef}
      flex={1}
      width="100%"
      margin="auto"
      maxHeight={maxHeight}
    />
  );
};
